import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiCall } from '../AuthContext';
import { Loader2, Users, X, Edit3, Trash2, Ban, CheckCircle2, CircleSlash, RefreshCw, UserCog, Eye } from 'lucide-react';

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // user object being edited
  const [form, setForm] = useState({ subscription_id: '', remaining_sku: '', expires_at: '', status: 'active' });
  const [saving, setSaving] = useState(false);
  const [actionBusy, setActionBusy] = useState(null);

  const fetchAll = async () => {
    try {
      const [u, p] = await Promise.all([
        apiCall('/api/users', { method: 'GET' }),
        apiCall('/api/subscriptions', { method: 'GET' }).catch(() => ({ data: [] })),
      ]);
      setUsers(u.data?.users || []);
      setPlans(p.data || []);
    } catch (e) {
      // ignore
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      subscription_id: u.subscription?.subscription_id || '',
      remaining_sku: u.subscription?.remaining_sku ?? '',
      expires_at: u.subscription?.expires_at ? u.subscription.expires_at.substring(0, 10) : '',
      status: u.subscription?.status || 'active',
    });
  };

  const saveAdjust = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (form.subscription_id && form.subscription_id !== editing.subscription?.subscription_id) {
        payload.subscription_id = form.subscription_id;
      }
      if (form.remaining_sku !== '' && Number(form.remaining_sku) !== editing.subscription?.remaining_sku) {
        payload.remaining_sku = Number(form.remaining_sku);
      }
      if (form.expires_at) payload.expires_at = new Date(form.expires_at).toISOString();
      if (form.status) payload.status = form.status;
      await apiCall(`/api/admin/users/${editing.username}/subscription`, { method: 'PUT', data: payload });
      setEditing(null);
      await fetchAll();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to update');
    } finally { setSaving(false); }
  };

  const cancelSub = async (u) => {
    if (!window.confirm(`Cancel subscription for ${u.username}?`)) return;
    setActionBusy(u.username);
    try {
      await apiCall(`/api/admin/users/${u.username}/cancel-subscription`, { method: 'POST' });
      await fetchAll();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); } finally { setActionBusy(null); }
  };

  const toggleAccess = async (u) => {
    setActionBusy(u.username);
    try {
      await apiCall(`/api/admin/users/${u.username}`, { method: 'PUT', data: { accessible: !u.accessible } });
      await fetchAll();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); } finally { setActionBusy(null); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Permanently delete ${u.username}? This removes all their data.`)) return;
    setActionBusy(u.username);
    try {
      await apiCall(`/api/admin/users/${u.username}`, { method: 'DELETE' });
      await fetchAll();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); } finally { setActionBusy(null); }
  };

  const impersonate = async (u, readonly = false) => {
    if (u.is_admin) return;
    const mode = readonly ? 'in read-only mode' : 'with full access';
    if (!window.confirm(`Login as ${u.username} ${mode}?\n\nYou will be signed in as this user. A banner will let you return to your admin account.`)) return;
    setActionBusy(u.username);
    try {
      const { data } = await apiCall(`/api/admin/impersonate/${u.username}`, {
        method: 'POST',
        data: { readonly },
      });
      if (data?.access_token) {
        // Replace stored token so apiCall + AuthContext both pick up the impersonated identity
        const stored = localStorage.getItem('xlabel_user');
        let parsed = {};
        try { parsed = stored ? JSON.parse(stored) : {}; } catch {}
        parsed.access_token = data.access_token;
        parsed.email = data.target_email || parsed.email;
        parsed.username = data.target_username || parsed.username;
        localStorage.setItem('xlabel_user', JSON.stringify(parsed));
      }
      // Hard reload so AuthContext re-hydrates from /auth/me with the new impersonation token
      window.location.href = '/dashboard';
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to impersonate');
      setActionBusy(null);
    }
  };

  const statusBadge = (s) => {
    const map = { active: 'bg-green-100 text-green-700', cancelled: 'bg-red-100 text-red-700', expired: 'bg-slate-100 text-slate-600' };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${map[s] || 'bg-slate-100 text-slate-600'}`}>{s || '—'}</span>;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-[#0052FF]" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#0F172A]">Customer Management</h2>
          <p className="text-sm text-slate-500 mt-1">View, adjust, and manage every customer's account and subscription</p>
        </div>
        <button onClick={fetchAll} className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700" data-testid="refresh-users">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">User</th>
                <th className="text-left px-4 py-3 font-semibold">Plan</th>
                <th className="text-left px-4 py-3 font-semibold">SKU Remaining</th>
                <th className="text-left px-4 py-3 font-semibold">Expires</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold">Access</th>
                <th className="text-right px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && <tr><td colSpan={7} className="px-6 py-8 text-center text-sm text-slate-400">No users found</td></tr>}
              {users.map(u => {
                const sub = u.subscription || {};
                const planName = plans.find(p => p.subscription_id === sub.subscription_id)?.subscription_name || sub.subscription_id || '—';
                return (
                  <tr key={u.username} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`user-row-${u.username}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#0F172A]">{u.username} {u.is_admin && <span className="text-xs text-[#0052FF] font-semibold ml-1">ADMIN</span>}</div>
                      <div className="text-xs text-slate-500">{u.email}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{planName}</td>
                    <td className="px-4 py-3 text-slate-700">{sub.remaining_sku ?? 0} / {sub.allowed_sku ?? 0}</td>
                    <td className="px-4 py-3 text-slate-600 text-xs">{sub.expires_at ? new Date(sub.expires_at).toLocaleDateString() : '—'}</td>
                    <td className="px-4 py-3">{statusBadge(sub.status)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${u.accessible ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {u.accessible ? 'Allowed' : 'Blocked'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Link
                          to={`/admin-users/${u.username}`}
                          className="p-1.5 rounded-lg text-slate-500 hover:bg-[#0052FF]/10 hover:text-[#0052FF]"
                          title="View products & batches"
                          data-testid={`view-user-${u.username}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Link>
                        {!u.is_admin && (
                          <button
                            onClick={() => impersonate(u, false)}
                            disabled={actionBusy === u.username}
                            className="p-1.5 rounded-lg text-slate-500 hover:bg-amber-50 hover:text-amber-700 disabled:opacity-50"
                            title="Login as this user (impersonate)"
                            data-testid={`impersonate-${u.username}`}
                          >
                            <UserCog className="w-4 h-4" />
                          </button>
                        )}
                        <button onClick={() => openEdit(u)} className="p-1.5 rounded-lg text-slate-500 hover:bg-[#0052FF]/10 hover:text-[#0052FF]" title="Adjust subscription" data-testid={`adjust-sub-${u.username}`}>
                          <Edit3 className="w-4 h-4" />
                        </button>
                        {sub.subscription_id && sub.subscription_id !== '1000' && sub.status !== 'cancelled' && (
                          <button onClick={() => cancelSub(u)} disabled={actionBusy === u.username}
                            className="p-1.5 rounded-lg text-slate-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-50" title="Cancel subscription" data-testid={`cancel-sub-${u.username}`}>
                            <Ban className="w-4 h-4" />
                          </button>
                        )}
                        <button onClick={() => toggleAccess(u)} disabled={actionBusy === u.username || u.is_admin}
                          className={`p-1.5 rounded-lg disabled:opacity-50 ${u.accessible ? 'text-slate-500 hover:bg-red-50 hover:text-red-600' : 'text-slate-500 hover:bg-green-50 hover:text-green-700'}`}
                          title={u.accessible ? 'Block access' : 'Restore access'} data-testid={`toggle-access-${u.username}`}>
                          {u.accessible ? <CircleSlash className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                        </button>
                        <button onClick={() => deleteUser(u)} disabled={actionBusy === u.username || u.is_admin}
                          className="p-1.5 rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40" title="Delete user" data-testid={`delete-user-${u.username}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Adjust modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="adjust-sub-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h3 className="text-lg font-bold text-[#0F172A]">Adjust Subscription</h3>
              <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <p className="text-xs text-slate-500 mb-1">Customer</p>
                <p className="text-sm font-semibold text-[#0F172A]">{editing.username} · {editing.email}</p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Plan</label>
                <select value={form.subscription_id} onChange={e => setForm(p => ({ ...p, subscription_id: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-[#0052FF] text-sm">
                  <option value="">— Keep current —</option>
                  {plans.map(p => (
                    <option key={p.subscription_id} value={p.subscription_id}>{p.subscription_name} ({p.allowed_sku_calls} SKUs)</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Remaining SKU</label>
                  <input type="number" value={form.remaining_sku} onChange={e => setForm(p => ({ ...p, remaining_sku: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-[#0052FF] text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Expires On</label>
                  <input type="date" value={form.expires_at} onChange={e => setForm(p => ({ ...p, expires_at: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-[#0052FF] text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Status</label>
                <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-[#0052FF] text-sm">
                  <option value="active">Active</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="expired">Expired</option>
                </select>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
              <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-lg text-sm text-slate-700 hover:bg-slate-200">Cancel</button>
              <button onClick={saveAdjust} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-[#0052FF] text-white hover:bg-[#0040CC] disabled:opacity-50" data-testid="save-adjust">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
